import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const C = {
  primary: '#2c3e50',
  primaryHover: '#1a252f',
  bg: '#f8f9fa',
  white: '#ffffff',
  border: '#e9ecef',
  muted: '#6c757d',
  light: '#adb5bd',
  green: '#10b981',
  amber: '#d97706',
  red: '#ef4444',
};

const EXERCISE_GROUPS = [
  { category: 'Lower Body — Squats', exercises: ['Air Squat', 'Goblet Squat', 'Overhead Squat', 'Jump Squat', 'Pistol Squat'] },
  { category: 'Lower Body — Lunges', exercises: ['Forward Lunge', 'Reverse Lunge', 'Walking Lunges', 'Lateral Lunge'] },
  { category: 'Lower Body — Hinges & Glutes', exercises: ['Glute Bridge', 'Single-leg Glute Bridge', 'Hip Thrust'] },
  { category: 'Upper Body — Push', exercises: ['Push-up', 'Incline Push-up', 'Decline Push-up', 'Diamond Push-up', 'Pike Push-up'] },
  { category: 'Upper Body — Pull', exercises: ['Pull-up', 'Chin-up', 'Inverted Row'] },
  { category: 'Core', exercises: ['Plank', 'Side Plank', 'Sit-ups', 'Crunches', 'Mountain Climbers', 'Leg Raises', 'Bicycle Crunch', 'Dead Bug'] },
  { category: 'Plyometrics', exercises: ['Box Jump', 'Lateral Bounds', 'Tuck Jumps', 'Broad Jump', 'Skater Jumps', 'Jump Lunges'] },
  { category: 'Agility & Movement', exercises: ['Side Shuffle', 'High Knees', 'Butt Kicks', 'Agility Ladder Drill', 'Split Step'] },
  { category: 'Balance & Stability', exercises: ['Single-leg Balance', 'Single-leg Deadlift', 'Y-Balance Test', 'Heel-to-Toe Walk'] },
];

const exerciseCategoryMap = Object.fromEntries(
  EXERCISE_GROUPS.flatMap(g => g.exercises.map(e => [e, g.category]))
);

export default function ExerciseSelectorScreen() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState('');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(
    Object.fromEntries(EXERCISE_GROUPS.map(g => [g.category, true]))
  );
  const [analyzedExercises, setAnalyzedExercises] = useState(new Set());
  const [recentExercises, setRecentExercises] = useState([]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('uploadedVideos') || '[]');
      setAnalyzedExercises(new Set(saved.filter(v => v.result).map(v => v.exerciseName)));
      setRecentExercises(JSON.parse(localStorage.getItem('recentExercises') || '[]').slice(0, 4));
    } catch (_) {}
  }, []);

  const handleSubmit = () => {
    if (!selected) return;
    try {
      const recent = JSON.parse(localStorage.getItem('recentExercises') || '[]');
      localStorage.setItem('recentExercises', JSON.stringify([selected, ...recent.filter(e => e !== selected)].slice(0, 8)));
    } catch (_) {}
    navigate('/upload', { state: { exerciseName: selected } });
  };

  const allExercises = EXERCISE_GROUPS.flatMap(g => g.exercises);
  const filtered = search ? allExercises.filter(e => e.toLowerCase().includes(search.toLowerCase())) : null;

  const ExerciseBtn = ({ exercise }) => {
    const isSel = selected === exercise;
    const wasDone = analyzedExercises.has(exercise);
    return (
      <button onClick={() => setSelected(exercise)} style={{
        padding: '9px 12px',
        backgroundColor: isSel ? C.primary : C.white,
        border: `1.5px solid ${isSel ? C.primary : C.border}`,
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: '500',
        color: isSel ? C.white : C.primary,
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '6px',
        width: '100%',
      }}>
        <span style={{ flex: 1, lineHeight: 1.3 }}>{exercise}</span>
        {wasDone && !isSel && <span style={{ width: '6px', height: '6px', backgroundColor: C.green, borderRadius: '50%', flexShrink: 0 }} />}
        {isSel && <span style={{ fontSize: '13px', fontWeight: '700' }}>✓</span>}
      </button>
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.white }}>
      {/* Header */}
      <div style={{ backgroundColor: C.white, borderBottom: `1px solid ${C.border}`, padding: '20px 20px 16px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: C.primary, margin: '0 0 4px' }}>Movement Analysis</h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Select an exercise to analyze your form</p>
      </div>

      <div style={{ padding: '16px 20px' }}>
        {/* Search bar — SVG icon (#3) */}
        <div style={{ position: 'relative', marginBottom: '20px' }}>
          <svg style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: C.light, pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search exercises..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '12px 40px 12px 42px', border: `1.5px solid ${C.border}`, borderRadius: '12px', fontSize: '14px', backgroundColor: C.white, outline: 'none', color: C.primary, boxSizing: 'border-box' }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.light, fontSize: '20px', lineHeight: 1, padding: '0 4px' }}>×</button>
          )}
        </div>

        {/* Recent exercises (#1) */}
        {!search && recentExercises.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>Recent</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {recentExercises.map(ex => (
                <button key={ex} onClick={() => setSelected(ex)} style={{
                  padding: '7px 14px',
                  backgroundColor: selected === ex ? C.primary : C.bg,
                  border: `1.5px solid ${selected === ex ? C.primary : C.border}`,
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: selected === ex ? C.white : C.primary,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  {ex}
                  {analyzedExercises.has(ex) && selected !== ex && (
                    <span style={{ width: '5px', height: '5px', backgroundColor: C.green, borderRadius: '50%' }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search results with category context (#9) */}
        {filtered ? (
          <div>
            <p style={{ fontSize: '12px', color: C.muted, marginBottom: '12px' }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</p>
            {filtered.length === 0
              ? <p style={{ textAlign: 'center', color: C.light, fontSize: '14px', padding: '40px 0' }}>No exercises found</p>
              : <div style={{ display: 'grid', gap: '8px' }}>
                  {filtered.map(ex => (
                    <div key={ex}>
                      <ExerciseBtn exercise={ex} />
                      {/* Category context tag (#9) */}
                      <p style={{ fontSize: '11px', color: C.light, marginTop: '3px', marginLeft: '4px' }}>{exerciseCategoryMap[ex]}</p>
                    </div>
                  ))}
                </div>
            }
          </div>
        ) : (
          /* Collapsible categories — collapsed by default (#5) */
          EXERCISE_GROUPS.map(group => {
            const isOpen = !collapsed[group.category];
            return (
              <div key={group.category} style={{ marginBottom: '8px' }}>
                <button
                  onClick={() => setCollapsed(prev => ({ ...prev, [group.category]: !prev[group.category] }))}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '13px 16px',
                    backgroundColor: isOpen ? C.primary : C.white,
                    border: `1.5px solid ${isOpen ? C.primary : C.border}`,
                    borderRadius: isOpen ? '12px 12px 0 0' : '12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: isOpen ? C.white : C.primary,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span>{group.category}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Count badge (#8) */}
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: isOpen ? 'rgba(255,255,255,0.2)' : C.bg,
                      color: isOpen ? C.white : C.muted,
                      padding: '2px 7px',
                      borderRadius: '10px',
                    }}>{group.exercises.length}</span>
                    <svg style={{ width: '14px', height: '14px', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {isOpen && (
                  <div style={{
                    backgroundColor: C.white,
                    border: `1.5px solid ${C.primary}`,
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: '12px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: '8px',
                  }}>
                    {group.exercises.map(ex => <ExerciseBtn key={ex} exercise={ex} />)}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Continue button — shows exercise name (#7) */}
      {selected && (
        <div style={{ position: 'sticky', bottom: 0, padding: '12px 20px 20px', backgroundColor: C.white, borderTop: `1px solid ${C.border}`, boxShadow: '0 -4px 16px rgba(0,0,0,0.06)' }}>
          <button
            onClick={handleSubmit}
            style={{
              width: '100%',
              backgroundColor: C.primary,
              color: C.white,
              border: 'none',
              borderRadius: '14px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: '12px',
              paddingBottom: '12px',
              gap: '2px',
            }}
          >
            <span style={{ fontSize: '15px', fontWeight: '700' }}>Continue →</span>
            <span style={{ fontSize: '12px', opacity: 0.75, fontWeight: '400' }}>{selected}</span>
          </button>
        </div>
      )}
    </div>
  );
}
